/**
 * useAdminComments - Comment state and CRUD for an admin report
 *
 * Extracted from useAdminReportData.js. Owns comment list state and
 * add/edit/delete actions (edit/delete limited to a 15-minute window
 * server-side).
 *
 * @param {string} uuid - The report UUID
 * @param {number} timestamp - The report timestamp (sort key)
 * @param {Object} deps
 * @param {Function} deps.fetchReport - Refetch the report (source of truth for comments)
 * @param {Function} deps.clearEscalationState - Reset escalation UI state after actions that restart the clock
 */

import { useState, useCallback } from 'react';

import Util from '../util/util';
import { showSuccess } from '../util/error/hookErrors';
import { trackEngagementAsync } from '../util/engagementTracker';
import { t } from '../i18n';

const useAdminComments = (uuid, timestamp, { fetchReport, clearEscalationState }) => {
  const [comments, setComments] = useState([]);
  const [commentsProp, setCommentsProp] = useState([]);

  /** Populate comment state from a fetched report */
  const initializeComments = useCallback((report) => {
    if (Array.isArray(report.comments)) {
      setComments(report.comments);
      setCommentsProp(report.comments);
    } else {
      setComments([]);
      setCommentsProp([]);
    }
  }, []);

  /** Refetch the report and sync comment state from it */
  const refreshComments = useCallback(async () => {
    const updatedReport = await fetchReport();
    if (updatedReport && Array.isArray(updatedReport.comments)) {
      setComments(updatedReport.comments);
      setCommentsProp(updatedReport.comments);
    }
  }, [fetchReport]);

  /**
   * Add a comment to the report
   */
  const addComment = useCallback(
    async (commentText, adminOnly = false) => {
      if (!commentText.trim()) return null;

      const requestData = {
        queryParams: {
          uuid: uuid,
          timestamp: timestamp,
          comment: JSON.stringify(commentText),
          IsNew: true,
          reportId: Util.formatUuid(uuid),
          setFirstCommentDate: !commentsProp || commentsProp.length === 0,
          commentDate: Date.now(),
          adminOnly: adminOnly,
        },
      };

      try {
        const data = await Util.post('Resty', '/items/addComment', requestData);
        clearEscalationState();
        showSuccess(adminOnly ? t('reports:ticket.adminCommentAdded') : t('reports:ticket.commentAdded'));

        // Track comment engagement for surprise badges (shows toast if badge earned)
        trackEngagementAsync('comment_reply');

        await Util.setReportCache(uuid, 'reportChanged', true);
        await Util.setReportCache(uuid, 'reportCommented', true);

        await refreshComments();

        return data.items;
      } catch (err) {
        Util.printAPIError(err);
        return null;
      }
    },
    [uuid, timestamp, commentsProp, refreshComments, clearEscalationState]
  );

  /**
   * Edit an existing comment (within 15 minute window)
   */
  const editComment = useCallback(
    async (commentId, newText) => {
      if (!commentId || !newText?.trim()) return null;

      try {
        const data = await Util.patch('Resty', `/items/comments/${commentId}`, {
          queryParams: {
            uuid: uuid,
            timestamp: timestamp,
          },
          body: {
            comment: newText.trim(),
          },
        });

        clearEscalationState();
        showSuccess(t('reports:ticket.commentUpdated'));

        await refreshComments();

        return data.comment;
      } catch (err) {
        // Check for specific error messages
        const errorMessage = err.error || err.message || t('reports:ticket.commentEditFailed');
        if (err.editWindowExpired) {
          throw new Error(t('reports:ticket.commentEditWindow'));
        }
        Util.printAPIError(err);
        throw new Error(errorMessage);
      }
    },
    [uuid, timestamp, refreshComments, clearEscalationState]
  );

  /**
   * Delete a comment (within 15 minute window)
   */
  const deleteComment = useCallback(
    async (commentId) => {
      if (!commentId) return false;

      try {
        await Util.del('Resty', `/items/comments/${commentId}`, {
          queryParams: {
            uuid: uuid,
            timestamp: timestamp,
          },
        });

        showSuccess(t('reports:ticket.commentDeleted'));

        await refreshComments();

        return true;
      } catch (err) {
        // Check for specific error messages
        const errorMessage = err.error || err.message || t('reports:ticket.commentDeleteFailed');
        if (err.deleteWindowExpired) {
          throw new Error(t('reports:ticket.commentDeleteWindow'));
        }
        Util.printAPIError(err);
        throw new Error(errorMessage);
      }
    },
    [uuid, timestamp, refreshComments]
  );

  return {
    comments,
    setComments,
    commentsProp,
    initializeComments,
    addComment,
    editComment,
    deleteComment,
  };
};

export default useAdminComments;
